/**
 * Models for the Assignments tab (Tab 2) of the Station Assignment workflow.
 *
 * These types are UI-oriented: they are shaped to fit the Assignments page's
 * rendering and pure-helper logic, not a specific backend schema. When a real
 * backend arrives, a thin adapter can map its payloads into these shapes.
 */

/**
 * A single associate in the assignments view.
 *
 * `assignedWorkstationId` is a convenience marker kept in sync by the
 * assignment helpers. The authoritative location for an associate is the
 * process-path / workstation structure itself (see `GetAssignmentsResponse`).
 */
export interface AssignmentAssociate {
  associateId: string;
  associateName: string;
  avatarUrl?: string;
  isClocked: boolean;
  hasWarning: boolean;
  trainedPaths: string[];
  assignedWorkstationId?: string;
}

/**
 * A workstation within a direct process path (e.g. "Primary line 1").
 */
export interface Workstation {
  workstationId: string;
  workstationName: string;
  assignedAssociates: AssignmentAssociate[];
  capacity: number;
}

/**
 * Per-process-path metrics row. Fields marked optional apply only to certain
 * path types:
 *   - `totalFlow`, `avgRatePerHour` are used for direct paths.
 *   - `dr`, `avgScanRate`, `unitsPerHour`, `processedPackages` are used for
 *     indirect paths.
 */
export interface ProcessPathMetrics {
  totalFlow?: number;
  actual: number;
  assigned: number;
  delta: number;
  avgRatePerHour?: number;
  dr?: number;
  avgScanRate?: number;
  unitsPerHour?: number;
  processedPackages?: number;
}

/**
 * A single process path (e.g. Primary, Clean decant, Problem solve).
 *
 * `workstations` is always present. For `pathType: 'indirect'` paths it is
 * `[]`, and the path's associates live in `indirectAssociates` instead.
 */
export interface ProcessPath {
  processPathId: string;
  processPathName: string;
  pathType: 'direct' | 'indirect';
  metrics: ProcessPathMetrics;
  workstations: Workstation[];
  indirectAssociates?: AssignmentAssociate[];
}

/**
 * Aggregate metrics displayed in the top-level MetricsBar.
 */
export interface AssignmentsMetrics {
  totalUnlocked: number;
  totalClockedIn: number;
  totalActual: number;
  assignedPrimary: number;
  assignedProblemSolve: number;
  assignedResale: number;
  assignedCleanDecant: number;
  assignedIndirect: number;
}

/**
 * Request payload for the `/getAssignments` endpoint.
 */
export interface GetAssignmentsRequest {
  nodeId: string;
  ofdDate: string;
  clockWindowName: string;
  planId: string;
}

/**
 * Response from the `/getAssignments` endpoint.
 *
 * `associates` is the canonical master list of every associate for this
 * shift; it contains both assigned and unassigned associates. An associate
 * is "unassigned" if they do not appear in any workstation's
 * `assignedAssociates` or any indirect path's `indirectAssociates`.
 */
export interface GetAssignmentsResponse {
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  metrics: AssignmentsMetrics;
  lastModifiedBy: string;
  lastModifiedDate: string;
}

/**
 * Empty response used as the default/error fallback in `api.ts`.
 */
export const EMPTY_RESPONSE: GetAssignmentsResponse = {
  associates: [],
  processPaths: [],
  metrics: {
    totalUnlocked: 0,
    totalClockedIn: 0,
    totalActual: 0,
    assignedPrimary: 0,
    assignedProblemSolve: 0,
    assignedResale: 0,
    assignedCleanDecant: 0,
    assignedIndirect: 0,
  },
  lastModifiedBy: '',
  lastModifiedDate: '',
};
